import React, { useState, useEffect } from 'react';
import { apiClient, uploadFile as apiUploadFile } from '../../api/client';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3000';

interface ImageEntry { url: string; name: string; }

interface LayoutArticle {
  id: string;
  sectionId: string;
  title: string;
  authorName: string | null;
  sortOrder: number;
  fileUrl: string | null;
  fileName: string | null;
  fileAvailable: boolean | null;
  imageUrls: ImageEntry[] | null;
  notes: string | null;
  vendorFeedback: string | null;
  designSuggestions: string | null;
  status: string;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
}

interface LayoutSection {
  id: string;
  name: string;
  sortOrder: number;
  description: string | null;
  articles: LayoutArticle[];
}

interface VendorQuestion {
  id: string;
  scope: string;
  scopeId: string | null;
  question: string;
  response: string | null;
  status: string;
  createdAt: string;
  vendorAccess?: { vendorName: string };
}

const ARTICLE_STATUSES = ['Pending', 'In Progress', 'Final', 'Approved', 'Canceled'];
const STATUS_COLORS: Record<string, string> = { Pending: '#9e9e9e', 'In Progress': '#f59e0b', Final: '#1a6b3c', Approved: '#2e7d32', Canceled: '#d32f2f' };

interface ProductionLayoutTabProps { canEdit: boolean; }

export function ProductionLayoutTab({ canEdit }: ProductionLayoutTabProps) {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [mobilePane, setMobilePane] = useState<'sections' | 'articles' | 'details'>('sections');

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  const [sections, setSections] = useState<LayoutSection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [vendorQuestions, setVendorQuestions] = useState<VendorQuestion[]>([]);

  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);
  const [selectedArticleId, setSelectedArticleId] = useState<string | null>(null);
  const [hoveredSectionId, setHoveredSectionId] = useState<string | null>(null);
  const [hoveredArticleId, setHoveredArticleId] = useState<string | null>(null);

  // Add section
  const [showAddSection, setShowAddSection] = useState(false);
  const [newSectionName, setNewSectionName] = useState('');
  // Edit section
  const [editingSectionId, setEditingSectionId] = useState<string | null>(null);
  const [editSectionName, setEditSectionName] = useState('');
  // Add article
  const [showAddArticle, setShowAddArticle] = useState(false);
  const [newArticleTitle, setNewArticleTitle] = useState('');
  // Edit article popup
  const [editPopupArticle, setEditPopupArticle] = useState<LayoutArticle | null>(null);
  const [epTitle, setEpTitle] = useState('');
  const [epAuthor, setEpAuthor] = useState('');
  const [epStatus, setEpStatus] = useState('');
  const [epNotes, setEpNotes] = useState('');
  const [epFileUrl, setEpFileUrl] = useState('');
  const [epFileName, setEpFileName] = useState('');
  const [epImageUrls, setEpImageUrls] = useState<ImageEntry[]>([]);
  const [epDesignSuggestions, setEpDesignSuggestions] = useState('');
  const [epSaving, setEpSaving] = useState(false);

  // Vendor feedback state
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackEditing, setFeedbackEditing] = useState(false);
  const [feedbackSaving, setFeedbackSaving] = useState(false);

  // Gallery view
  const [showGallery, setShowGallery] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
  const [lightboxTitle, setLightboxTitle] = useState('');
  const [lightboxArticle, setLightboxArticle] = useState<LayoutArticle | null>(null);
  const [lightboxSection, setLightboxSection] = useState('');

  // File viewer popup
  const [showFileViewer, setShowFileViewer] = useState(false);

  // Memorial Tiles
  const [showMemorialTiles, setShowMemorialTiles] = useState(false);
  const [memorialTiles, setMemorialTiles] = useState<Array<{ orderedBy: string; inMemoryOf: string; inscription: string }>>(() => {
    try {
      const saved = localStorage.getItem('memorial_tiles_data');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [showTilesPreview, setShowTilesPreview] = useState(false);

  // Persist tiles to localStorage when they change
  useEffect(() => {
    if (memorialTiles.length > 0) {
      localStorage.setItem('memorial_tiles_data', JSON.stringify(memorialTiles));
    }
  }, [memorialTiles]);

  useEffect(() => { fetchLayout(); fetchVendorQuestions(); }, []);

  async function fetchLayout() {
    setLoading(true);
    try {
      const data = await apiClient<LayoutSection[]>('/production-layout/sections');
      setSections(data);
      if (data.length > 0 && !selectedSectionId) setSelectedSectionId(data[0].id);
    } catch { setError('Failed to load production layout.'); }
    finally { setLoading(false); }
  }
  async function fetchVendorQuestions() {
    try { setVendorQuestions(await apiClient<VendorQuestion[]>('/vendor-portal/admin/questions')); } catch {}
  }

  async function handleSaveFeedback(articleId: string) {
    setFeedbackSaving(true);
    try {
      await apiClient(`/vendor-portal/admin/articles/${articleId}/feedback`, {
        method: 'PATCH',
        body: { vendorFeedback: feedbackText },
      });
      setFeedbackEditing(false);
      await fetchLayout();
    } catch { setError('Failed to save feedback.'); }
    finally { setFeedbackSaving(false); }
  }

  const selectedSection = sections.find((s) => s.id === selectedSectionId) || null;
  const rawArticles = selectedSection?.articles || [];
  // Sort: active articles by sortOrder, deleted at bottom
  const articles = [...rawArticles].sort((a, b) => {
    if (a.isDeleted !== b.isDeleted) return a.isDeleted ? 1 : -1;
    return a.sortOrder - b.sortOrder;
  });
  const selectedArticle = articles.find((a) => a.id === selectedArticleId) || null;
  const articleQuestions = vendorQuestions.filter(
    (q) => (q.scope === 'Article' && q.scopeId === selectedArticleId) ||
           (q.scope === 'Section' && q.scopeId === selectedSectionId) ||
           (q.scope === 'General' && selectedArticleId)
  );

  // Section actions
  async function handleAddSection() {
    if (!newSectionName.trim()) return;
    try { const c = await apiClient<LayoutSection>('/production-layout/sections', { method: 'POST', body: { name: newSectionName.trim() } }); setNewSectionName(''); setShowAddSection(false); fetchLayout(); setSelectedSectionId(c.id); } catch { setError('Failed to create section.'); }
  }
  async function handleDeleteSection(id: string) {
    if (!confirm('Delete this section and all its articles?')) return;
    try { await apiClient(`/production-layout/sections/${id}`, { method: 'DELETE' }); if (selectedSectionId === id) { setSelectedSectionId(null); setSelectedArticleId(null); } fetchLayout(); } catch { setError('Failed to delete section.'); }
  }
  async function handleMoveSection(index: number, dir: 'up' | 'down') {
    const arr = [...sections]; const sw = dir === 'up' ? index - 1 : index + 1;
    if (sw < 0 || sw >= arr.length) return;
    [arr[index], arr[sw]] = [arr[sw], arr[index]]; setSections(arr);
    try { await apiClient('/production-layout/sections/reorder', { method: 'POST', body: { orderedIds: arr.map(s => s.id) } }); } catch { fetchLayout(); }
  }
  async function handleRenameSection(id: string) {
    if (!editSectionName.trim()) return;
    try { await apiClient(`/production-layout/sections/${id}`, { method: 'PATCH', body: { name: editSectionName.trim() } }); setEditingSectionId(null); fetchLayout(); } catch { setError('Failed to rename section.'); }
  }

  // Article actions
  async function handleAddArticle() {
    if (!newArticleTitle.trim() || !selectedSectionId) return;
    try { await apiClient(`/production-layout/sections/${selectedSectionId}/articles`, { method: 'POST', body: { title: newArticleTitle.trim() } }); setNewArticleTitle(''); setShowAddArticle(false); fetchLayout(); } catch { setError('Failed to add article.'); }
  }
  async function handleSoftDelete(id: string) {
    if (!confirm('Remove this article? It will be crossed out but not lost.')) return;
    try { await apiClient(`/production-layout/articles/${id}`, { method: 'DELETE' }); fetchLayout(); } catch { setError('Failed to remove article.'); }
  }
  async function handleRestore(id: string) {
    try { await apiClient(`/production-layout/articles/${id}/restore`, { method: 'PATCH' }); fetchLayout(); } catch { setError('Failed to restore article.'); }
  }
  async function handleMoveArticle(index: number, dir: 'up' | 'down') {
    const active = articles.filter(a => !a.isDeleted);
    const sw = dir === 'up' ? index - 1 : index + 1;
    if (sw < 0 || sw >= active.length) return;
    const arr = [...active]; [arr[index], arr[sw]] = [arr[sw], arr[index]];
    setSections(prev => prev.map(sec => sec.id === selectedSectionId ? { ...sec, articles: [...arr, ...articles.filter(a => a.isDeleted)] } : sec));
    try { await apiClient(`/production-layout/sections/${selectedSectionId}/articles/reorder`, { method: 'POST', body: { orderedIds: arr.map(a => a.id) } }); } catch { fetchLayout(); }
  }

  // Print infographic
  function handlePrintInfographic() {
    const totalArticles = sections.reduce((sum, s) => sum + s.articles.filter(a => !a.isDeleted).length, 0);
    const sectionRows = sections.map((section, sIdx) => {
      const activeArticles = section.articles.filter(a => !a.isDeleted);
      const articleRows = activeArticles.map((a, aIdx) => `
        <tr>
          <td style="padding:5px 10px;border-bottom:1px solid #eee;font-size:12px;color:#666;">${sIdx + 1}.${aIdx + 1}</td>
          <td style="padding:5px 10px;border-bottom:1px solid #eee;font-size:12px;">${a.title}</td>
          <td style="padding:5px 10px;border-bottom:1px solid #eee;font-size:12px;color:#555;">${a.authorName || '—'}</td>
          <td style="padding:5px 10px;border-bottom:1px solid #eee;font-size:11px;text-align:center;">
            <span style="padding:2px 8px;border-radius:3px;font-weight:600;color:#fff;background:${STATUS_COLORS[a.status] || '#999'};font-size:10px;">${a.status}</span>
          </td>
          <td style="padding:5px 10px;border-bottom:1px solid #eee;font-size:11px;text-align:center;">${a.fileName ? '✓' : '—'}</td>
        </tr>
      `).join('');
      return `
        <tr style="background:#f0f7f2;">
          <td colspan="5" style="padding:8px 10px;font-weight:700;font-size:13px;color:#0f2d1f;border-bottom:2px solid #1a6b3c;">
            ${sIdx + 1}. ${section.name} <span style="font-weight:400;font-size:11px;color:#666;">(${activeArticles.length} articles)</span>
          </td>
        </tr>
        ${articleRows}
      `;
    }).join('');

    const html = `<!DOCTYPE html><html><head><title>Weekend Booklet — Production Layout</title>
      <style>
        * { margin:0; padding:0; box-sizing:border-box; }
        body { font-family:'Segoe UI',system-ui,sans-serif; padding:30px 40px; color:#333; }
        .header { text-align:center; margin-bottom:24px; border-bottom:3px solid #1a6b3c; padding-bottom:16px; }
        .header h1 { font-size:22px; color:#0f2d1f; margin-bottom:4px; }
        .header p { font-size:12px; color:#666; }
        .stats { display:flex; justify-content:center; gap:30px; margin-bottom:20px; }
        .stat { text-align:center; }
        .stat-num { font-size:24px; font-weight:700; color:#1a6b3c; }
        .stat-label { font-size:10px; text-transform:uppercase; color:#888; letter-spacing:0.5px; }
        table { width:100%; border-collapse:collapse; }
        th { padding:6px 10px; text-align:left; font-size:10px; text-transform:uppercase; letter-spacing:0.5px; color:#666; border-bottom:2px solid #333; }
        .footer { margin-top:20px; text-align:center; font-size:10px; color:#aaa; border-top:1px solid #eee; padding-top:10px; }
        @media print { body { padding:15px 20px; } }
      </style>
    </head><body>
      <div class="header">
        <h1>📖 KDU Georgia 2026 — Weekend Booklet</h1>
        <p>Production Layout Overview • Generated ${new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
      </div>
      <div class="stats">
        <div class="stat"><div class="stat-num">${sections.length}</div><div class="stat-label">Sections</div></div>
        <div class="stat"><div class="stat-num">${totalArticles}</div><div class="stat-label">Articles</div></div>
        <div class="stat"><div class="stat-num">${sections.reduce((s, sec) => s + sec.articles.filter(a => !a.isDeleted && (a.status === 'Final' || a.status === 'Approved')).length, 0)}</div><div class="stat-label">Final/Approved</div></div>
        <div class="stat"><div class="stat-num">${totalArticles > 0 ? Math.round(sections.reduce((s, sec) => s + sec.articles.filter(a => !a.isDeleted && (a.status === 'Final' || a.status === 'Approved')).length, 0) / totalArticles * 100) : 0}%</div><div class="stat-label">Complete</div></div>
      </div>
      <table>
        <thead><tr><th>#</th><th>Article Title</th><th>Author</th><th style="text-align:center">Status</th><th style="text-align:center">File</th></tr></thead>
        <tbody>${sectionRows}</tbody>
      </table>
      <div class="footer">KDU Georgia 2026 Weekend Booklet — Production Layout Infographic</div>
    </body></html>`;
    const w = window.open('', '_blank');
    if (w) { w.document.write(html); w.document.close(); w.focus(); setTimeout(() => w.print(), 300); }
  }

  // Edit popup
  function openEditPopup(article: LayoutArticle) {
    setEditPopupArticle(article);
    setEpTitle(article.title);
    setEpAuthor(article.authorName || '');
    setEpStatus(article.status);
    setEpNotes(article.notes || '');
    setEpFileUrl(article.fileUrl || '');
    setEpFileName(article.fileName || '');
    setEpImageUrls(Array.isArray(article.imageUrls) ? article.imageUrls : []);
    setEpDesignSuggestions(article.designSuggestions || '');
  }
  async function handleSaveEdit() {
    if (!editPopupArticle) return;
    setEpSaving(true);
    try {
      await apiClient(`/production-layout/articles/${editPopupArticle.id}`, {
        method: 'PATCH',
        body: { title: epTitle, authorName: epAuthor, status: epStatus, notes: epNotes, designSuggestions: epDesignSuggestions || null, fileUrl: epFileUrl || null, fileName: epFileName || null, imageUrls: epImageUrls },
      });
      setEditPopupArticle(null); fetchLayout();
    } catch { setError('Failed to save article.'); }
    finally { setEpSaving(false); }
  }
  function addImageEntry() { setEpImageUrls([...epImageUrls, { url: '', name: '' }]); }
  function updateImageEntry(idx: number, field: 'url' | 'name', val: string) {
    setEpImageUrls(prev => prev.map((e, i) => i === idx ? { ...e, [field]: val } : e));
  }
  function removeImageEntry(idx: number) { setEpImageUrls(prev => prev.filter((_, i) => i !== idx)); }

  if (loading) return <div style={st.emptyState}>Loading production layout...</div>;

  return (
    <div style={st.container}>
      <div style={st.header}>
        <h3 style={st.title}>📖 Production Layout</h3>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button style={{ ...st.btn, backgroundColor: showMemorialTiles ? '#7c3aed' : '#555' }} onClick={() => { setShowMemorialTiles(!showMemorialTiles); if (!showMemorialTiles) { setShowGallery(false); if (memorialTiles.length > 0) setShowTilesPreview(true); } }}>🕯️ Memorial Tiles</button>
          <button style={{ ...st.btn, backgroundColor: showGallery ? '#0277bd' : '#555' }} onClick={() => { setShowGallery(!showGallery); if (!showGallery) setShowMemorialTiles(false); }}>🖼️ {showGallery ? 'List View' : 'Gallery'}</button>
          <button style={{ ...st.btn, backgroundColor: '#555' }} onClick={handlePrintInfographic}>🖨️ Infographic</button>
          {canEdit && <button style={st.btn} onClick={() => setShowAddSection(true)}>+ Add Section</button>}
        </div>
      </div>
      {error && <div style={st.error}>{error}</div>}

      {/* Gallery View */}
      {showGallery && (
        <div style={{ padding: '1rem 0' }}>
          <div style={{ fontSize: '0.82rem', color: '#6b7280', marginBottom: '0.75rem' }}>
            {sections.flatMap(s => s.articles.filter(a => a.fileUrl && !a.isDeleted)).length} pages uploaded — ordered by section and position
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '14px' }}>
            {sections.flatMap((section, sIdx) =>
              section.articles
                .filter(a => a.fileUrl && !a.isDeleted)
                .map((article, aIdx) => {
                  const apiBase = (import.meta as any).env?.VITE_API_URL || 'http://localhost:3000';
                  const imgSrc = article.fileUrl!.startsWith('/') ? `${apiBase}${article.fileUrl}` : article.fileUrl!;
                  const pageNum = sections.slice(0, sIdx).reduce((acc, s) => acc + s.articles.filter(a => a.fileUrl && !a.isDeleted).length, 0) + aIdx + 1;
                  return (
                    <div key={article.id} onClick={() => { setLightboxSrc(imgSrc); setLightboxTitle(`Page ${pageNum} — ${article.title}`); setLightboxArticle(article); setLightboxSection(section.name); }} style={{ borderRadius: '8px', border: '1px solid #e5e7eb', overflow: 'hidden', backgroundColor: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', cursor: 'pointer' }}>
                      <div style={{ position: 'relative', paddingTop: '140%', backgroundColor: '#f9fafb' }}>
                        {/\.pdf$/i.test(article.fileUrl!) ? (
                          <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#d32f2f' }}>
                            <span style={{ fontSize: '3rem' }}>📄</span>
                            <span style={{ fontSize: '0.75rem', fontWeight: 600, marginTop: '4px' }}>PDF</span>
                          </div>
                        ) : (
                          <img src={imgSrc} alt={article.title} loading="lazy" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'contain', imageRendering: 'auto', padding: '4px', boxSizing: 'border-box' }} />
                        )}
                        <div style={{ position: 'absolute', top: '6px', left: '6px', backgroundColor: '#1a6b3c', color: '#fff', fontSize: '0.7rem', fontWeight: 700, padding: '2px 8px', borderRadius: '10px' }}>
                          Page {pageNum}
                        </div>
                      </div>
                      <div style={{ padding: '8px 10px' }}>
                        <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#0f2d1f', marginBottom: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{article.title}</div>
                        <div style={{ fontSize: '0.65rem', color: '#6b7280' }}>{section.name}</div>
                      </div>
                    </div>
                  );
                })
            )}
          </div>
        </div>
      )}

      {/* Lightbox Modal — 2-pane: image left, comments right */}
      {lightboxSrc && (
        <div onClick={() => { setLightboxSrc(null); setLightboxArticle(null); }} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: '1rem' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ display: 'flex', width: '90vw', maxWidth: '1100px', height: '85vh', borderRadius: '10px', overflow: 'hidden', backgroundColor: '#fff', boxShadow: '0 8px 40px rgba(0,0,0,0.4)' }}>
            {/* Left: Image or PDF */}
            <div style={{ flex: '1 1 60%', backgroundColor: '#1a1a1a', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', position: 'relative' }}>
              {/\.pdf$/i.test(lightboxSrc) ? (
                <iframe src={lightboxSrc} title={lightboxTitle} style={{ width: '100%', height: '100%', border: 'none', borderRadius: '4px' }} />
              ) : (
                <img src={lightboxSrc} alt={lightboxTitle} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
              )}
              <div style={{ position: 'absolute', top: '10px', left: '10px', backgroundColor: '#1a6b3c', color: '#fff', fontSize: '0.72rem', fontWeight: 700, padding: '3px 10px', borderRadius: '10px' }}>{lightboxTitle}</div>
            </div>
            {/* Right: Comments */}
            <div style={{ flex: '0 0 320px', display: 'flex', flexDirection: 'column', borderLeft: '1px solid #e5e7eb', overflow: 'hidden' }}>
              <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
                <div style={{ fontSize: '0.9rem', fontWeight: 700, color: '#0f2d1f' }}>{lightboxArticle?.title || ''}</div>
                <div style={{ fontSize: '0.72rem', color: '#6b7280', marginTop: '2px' }}>{lightboxSection} &middot; Status: {lightboxArticle?.status}</div>
              </div>
              <div style={{ flex: 1, overflowY: 'auto', padding: '0.75rem 1rem' }}>
                {/* Editor Notes */}
                {lightboxArticle?.notes && (
                  <div style={{ marginBottom: '0.75rem' }}>
                    <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: '0.25rem' }}>Editor Notes</div>
                    <div style={{ fontSize: '0.8rem', color: '#333', backgroundColor: '#f3f4f6', padding: '0.5rem', borderRadius: '4px', whiteSpace: 'pre-wrap' }}>{lightboxArticle.notes}</div>
                  </div>
                )}
                {/* Vendor Feedback */}
                {lightboxArticle?.vendorFeedback && (
                  <div style={{ marginBottom: '0.75rem' }}>
                    <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#b45309', textTransform: 'uppercase', marginBottom: '0.25rem' }}>Feedback to Vendor</div>
                    <div style={{ fontSize: '0.8rem', color: '#92400e', backgroundColor: '#fff8e1', padding: '0.5rem', borderRadius: '4px', border: '1px solid #fde68a', whiteSpace: 'pre-wrap' }}>{lightboxArticle.vendorFeedback}</div>
                  </div>
                )}
                {/* Vendor Questions for this article */}
                {(() => {
                  const artQuestions = vendorQuestions.filter(q => q.scope === 'Article' && q.scopeId === lightboxArticle?.id);
                  if (artQuestions.length === 0) return <div style={{ fontSize: '0.75rem', color: '#999', fontStyle: 'italic' }}>No vendor comments on this page.</div>;
                  return (
                    <div>
                      <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: '0.25rem' }}>Vendor Comments ({artQuestions.length})</div>
                      {artQuestions.map(q => (
                        <div key={q.id} style={{ marginBottom: '0.5rem', padding: '0.4rem', backgroundColor: '#f9fafb', borderRadius: '4px', border: '1px solid #e5e7eb' }}>
                          <div style={{ fontSize: '0.72rem', color: '#333' }}>{q.question}</div>
                          {q.response && <div style={{ marginTop: '0.25rem', fontSize: '0.72rem', color: '#1a6b3c', fontWeight: 500 }}>Response: {q.response}</div>}
                          <div style={{ fontSize: '0.6rem', color: '#999', marginTop: '0.2rem' }}>{new Date(q.createdAt).toLocaleDateString()}</div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
              <div style={{ padding: '0.5rem 1rem', borderTop: '1px solid #e5e7eb', textAlign: 'right' }}>
                <button onClick={() => { setLightboxSrc(null); setLightboxArticle(null); }} style={{ padding: '0.4rem 1rem', backgroundColor: '#e0e0e0', border: 'none', borderRadius: '4px', fontSize: '0.8rem', cursor: 'pointer', color: '#333' }}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Memorial Tiles View */}
      {showMemorialTiles && (
        <div style={{ padding: '1rem 0' }}>
          <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            {memorialTiles.length > 0 && (
              <>
                <span style={{ fontSize: '0.82rem', color: '#6b7280' }}>{memorialTiles.length} tiles loaded (saved)</span>
                <button onClick={() => setShowTilesPreview(true)} style={{ padding: '0.5rem 1rem', backgroundColor: '#1a6b3c', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer' }}>
                  📄 Preview Pages ({Math.ceil(memorialTiles.length / 12)})
                </button>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '0.4rem 0.8rem', backgroundColor: '#e0e0e0', color: '#333', borderRadius: '4px', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer' }}>
                  📤 Upload New File
                  <input type="file" accept=".csv" style={{ display: 'none' }} onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const reader = new FileReader();
                    reader.onload = (ev) => {
                      const text = ev.target?.result as string;
                      const lines = text.split('\n').filter(l => l.trim());
                      const tiles: Array<{ orderedBy: string; inMemoryOf: string; inscription: string }> = [];
                      for (let i = 1; i < lines.length; i++) {
                        const match = lines[i].match(/^"?([^",]*)"?\s*,\s*"?([^"]*)"?\s*,\s*"?(.*?)"?\s*$/);
                        if (match) {
                          tiles.push({ orderedBy: match[1].trim(), inMemoryOf: match[2].trim(), inscription: match[3].trim().replace(/^"|"$/g, '') });
                        } else {
                          const parts = lines[i].split(',');
                          if (parts.length >= 3) {
                            tiles.push({ orderedBy: parts[0].trim(), inMemoryOf: parts[1].trim(), inscription: parts.slice(2).join(',').trim().replace(/^"|"$/g, '') });
                          }
                        }
                      }
                      setMemorialTiles(tiles);
                    };
                    reader.readAsText(file);
                    e.target.value = '';
                  }} />
                </label>
              </>
            )}
            {memorialTiles.length === 0 && (
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '0.5rem 1rem', backgroundColor: '#7c3aed', color: '#fff', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}>
                📄 Upload Memorial CSV
                <input type="file" accept=".csv" style={{ display: 'none' }} onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const reader = new FileReader();
                  reader.onload = (ev) => {
                    const text = ev.target?.result as string;
                    const lines = text.split('\n').filter(l => l.trim());
                    const tiles: Array<{ orderedBy: string; inMemoryOf: string; inscription: string }> = [];
                    for (let i = 1; i < lines.length; i++) {
                      const match = lines[i].match(/^"?([^",]*)"?\s*,\s*"?([^"]*)"?\s*,\s*"?(.*?)"?\s*$/);
                      if (match) {
                        tiles.push({ orderedBy: match[1].trim(), inMemoryOf: match[2].trim(), inscription: match[3].trim().replace(/^"|"$/g, '') });
                      } else {
                        const parts = lines[i].split(',');
                        if (parts.length >= 3) {
                          tiles.push({ orderedBy: parts[0].trim(), inMemoryOf: parts[1].trim(), inscription: parts.slice(2).join(',').trim().replace(/^"|"$/g, '') });
                        }
                      }
                    }
                    setMemorialTiles(tiles);
                  };
                  reader.readAsText(file);
                  e.target.value = '';
                }} />
              </label>
            )}
          </div>
          {memorialTiles.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '10px' }}>
              {memorialTiles.map((t, i) => (
                <div key={i} style={{ border: '1px solid #d1d5db', borderRadius: '10px', padding: '0.75rem', textAlign: 'center', backgroundColor: '#fff', boxShadow: '0 2px 4px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.8), inset 0 -1px 2px rgba(0,0,0,0.04)' }}>
                  <div style={{ fontSize: '1.3rem', marginBottom: '3px' }}>🪔</div>
                  <div style={{ fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: '#666', marginBottom: '3px' }}>In Memory Of</div>
                  <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#1a1a1a', marginBottom: '4px', lineHeight: 1.2 }}>{t.inMemoryOf}</div>
                  {t.inscription && t.inscription !== 'NONE' && (
                    <div style={{ fontSize: '0.65rem', fontStyle: 'italic', color: '#444', lineHeight: 1.3 }}>{t.inscription}</div>
                  )}
                  <div style={{ marginTop: '4px', fontSize: '0.55rem', color: '#999' }}>by {t.orderedBy}</div>
                </div>
              ))}
            </div>
          )}
          {memorialTiles.length === 0 && (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#999', fontSize: '0.85rem' }}>Upload a CSV file to generate memorial tiles.<br/>Format: Order placed by, In Memory of, Inscription</div>
          )}
        </div>
      )}

      {/* Memorial Tiles Preview Modal */}
      {showTilesPreview && memorialTiles.length > 0 && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: '#2d2d3d', display: 'flex', flexDirection: 'column', alignItems: 'center', zIndex: 2000, overflowY: 'auto' }}>
          {/* Fixed banner bar */}
          <div style={{ position: 'sticky', top: 0, width: '100%', backgroundColor: '#1a1a1a', borderBottom: '1px solid #333', padding: '0.75rem 1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem', zIndex: 1, flexShrink: 0 }}>
            <button onClick={() => {
              const TILES_PER_PAGE = 12;
              const pages: string[] = [];
              for (let p = 0; p < Math.ceil(memorialTiles.length / TILES_PER_PAGE); p++) {
                const pageTiles = memorialTiles.slice(p * TILES_PER_PAGE, (p + 1) * TILES_PER_PAGE);
                const tilesHtml = pageTiles.map(t => `
                  <div style="width:2.65in;height:2.13in;border:1px solid #999;border-radius:10px;display:flex;flex-direction:column;align-items:center;justify-content:flex-start;padding:0.1in;box-sizing:border-box;text-align:center;box-shadow:0 3px 8px rgba(0,0,0,0.15),inset 0 2px 0 rgba(255,255,255,0.7),inset 0 -2px 4px rgba(0,0,0,0.08);background:linear-gradient(180deg,#ffffff 0%,#f4f4f4 100%);">
                    <div style="font-size:32px;margin-bottom:10px;">🪔</div>
                    <div style="font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:2.5px;color:#444;margin-bottom:10px;">In Memory Of</div>
                    <div style="font-size:12px;font-weight:700;color:#1a1a1a;margin-bottom:8px;line-height:1.3;">${t.inMemoryOf}</div>
                    <div style="font-size:10px;font-style:italic;color:#333;line-height:1.4;max-width:2.3in;">${t.inscription !== 'NONE' ? t.inscription : ''}</div>
                  </div>`).join('');
                pages.push(`<div style="width:8.5in;height:11in;display:flex;flex-direction:column;box-sizing:border-box;page-break-after:always;position:relative;">
                  <div style="position:absolute;top:0;bottom:0;left:0.25in;border-left:1px dashed rgba(26,107,60,0.3);z-index:2;"></div>
                  <div style="position:absolute;top:0;bottom:0;right:0.25in;border-left:1px dashed rgba(26,107,60,0.3);z-index:2;"></div>
                  <div style="width:100%;height:1in;display:flex;align-items:center;justify-content:center;background:linear-gradient(135deg,#0d4f2b 0%,#1a6b3c 50%,#2e8b57 100%);">
                    <span style="font-size:44px;font-weight:700;letter-spacing:8px;text-transform:uppercase;color:#fff;font-family:Georgia,serif;">IN MEMORIAM</span>
                  </div>
                  <div style="flex:1;display:grid;grid-template-columns:repeat(3,2.65in);grid-template-rows:repeat(4,2.13in);gap:0.08in;justify-content:center;align-content:start;padding:0.12in 0.25in;">${tilesHtml}</div>
                  <div style="width:100%;height:1in;background:linear-gradient(135deg,#1a237e 0%,#283593 50%,#3949ab 100%);display:flex;align-items:center;justify-content:center;">
                    <span style="font-size:8px;color:rgba(255,255,255,0.6);letter-spacing:1px;">Page ${p + 1} of ${Math.ceil(memorialTiles.length / TILES_PER_PAGE)}</span>
                  </div>
                </div>`);
              }
              const html = `<!DOCTYPE html><html><head><title>Memorial Tiles</title><style>@media print{body{margin:0;}@page{margin:0;}}</style></head><body style="font-family:Georgia,serif;margin:0;">${pages.join('')}</body></html>`;
              const w = window.open('', '_blank');
              if (w) { w.document.write(html); w.document.close(); w.focus(); setTimeout(() => w.print(), 400); }
            }} style={{ padding: '0.5rem 1.2rem', backgroundColor: '#1a6b3c', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer' }}>
              🖨️ Print All Pages
            </button>
            <span style={{ color: '#ccc', fontSize: '0.78rem' }}>{Math.ceil(memorialTiles.length / 12)} page{Math.ceil(memorialTiles.length / 12) !== 1 ? 's' : ''} &middot; {memorialTiles.length} tiles &middot; 12 per page</span>
            <button onClick={() => setShowTilesPreview(false)} style={{ padding: '0.4rem 1rem', backgroundColor: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '4px', color: '#fff', fontSize: '0.78rem', cursor: 'pointer' }}>Close</button>
          </div>
          {/* Pages */}
          <div style={{ padding: '1.5rem 1rem' }}>
            {Array.from({ length: Math.ceil(memorialTiles.length / 12) }).map((_, pageIdx) => {
              const pageTiles = memorialTiles.slice(pageIdx * 12, (pageIdx + 1) * 12);
              const pageBg = pageIdx % 2 === 0 ? '#3a3a4a' : '#2a2a3a';
              return (
                <div key={pageIdx} style={{ backgroundColor: pageBg, padding: '1.25rem', borderRadius: '8px', marginBottom: '1.5rem' }}>
                <div style={{ width: '8.5in', minHeight: '11in', backgroundColor: '#fff', borderRadius: '4px', boxShadow: '0 4px 20px rgba(0,0,0,0.3)', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden', margin: '0 auto' }}>
                  {/* Left/Right margin guides */}
                  <div style={{ position: 'absolute', top: 0, bottom: 0, left: '0.25in', width: 0, borderLeft: '1px dashed rgba(26,107,60,0.3)', zIndex: 2 }} />
                  <div style={{ position: 'absolute', top: 0, bottom: 0, right: '0.25in', width: 0, borderLeft: '1px dashed rgba(26,107,60,0.3)', zIndex: 2 }} />
                  {/* Top green gradient header */}
                  <div style={{ width: '100%', height: '1in', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #0d4f2b 0%, #1a6b3c 50%, #2e8b57 100%)', flexShrink: 0 }}>
                    <span style={{ fontSize: '44px', fontWeight: 700, letterSpacing: '8px', textTransform: 'uppercase', color: '#fff', fontFamily: 'Georgia, serif' }}>IN MEMORIAM</span>
                  </div>
                  {/* Tiles grid — inside margins */}
                  <div style={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(3, 2.65in)', gridTemplateRows: 'repeat(4, 2.13in)', gap: '0.08in', justifyContent: 'center', alignContent: 'start', padding: '0.12in 0.25in 0.12in', position: 'relative', zIndex: 1 }}>
                    {pageTiles.map((t, i) => (
                      <div key={i} style={{ width: '2.65in', height: '2.13in', border: '1px solid #999', borderRadius: '10px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', paddingTop: '0.08in', paddingLeft: '0.1in', paddingRight: '0.1in', paddingBottom: '0.06in', boxSizing: 'border-box', textAlign: 'center', boxShadow: '0 3px 8px rgba(0,0,0,0.15), inset 0 2px 0 rgba(255,255,255,0.7), inset 0 -2px 4px rgba(0,0,0,0.08)', background: 'linear-gradient(180deg, #ffffff 0%, #f4f4f4 100%)' }}>
                        <div style={{ fontSize: '32px', marginBottom: '10px' }}>🪔</div>
                        <div style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '2.5px', color: '#444', marginBottom: '10px' }}>In Memory Of</div>
                        <div style={{ fontSize: '12px', fontWeight: 700, color: '#1a1a1a', marginBottom: '8px', lineHeight: 1.3, fontFamily: 'Georgia, serif' }}>{t.inMemoryOf}</div>
                        <div style={{ fontSize: '10px', fontStyle: 'italic', color: '#333', lineHeight: 1.4, maxWidth: '2.3in' }}>{t.inscription !== 'NONE' ? t.inscription : ''}</div>
                      </div>
                    ))}
                  </div>
                  {/* Bottom blue gradient footer */}
                  <div style={{ width: '100%', height: '1in', background: 'linear-gradient(135deg, #1a237e 0%, #283593 50%, #3949ab 100%)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ fontSize: '8px', color: 'rgba(255,255,255,0.6)', letterSpacing: '1px' }}>Page {pageIdx + 1} of {Math.ceil(memorialTiles.length / 12)}</span>
                  </div>
                </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* File Viewer Popup — 2-pane: Editor images | Vendor file */}
      {showFileViewer && selectedArticle && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: '1rem' }} onClick={() => setShowFileViewer(false)}>
          <div style={{ display: 'flex', width: '90vw', maxWidth: '1000px', height: '80vh', borderRadius: '10px', overflow: 'hidden', backgroundColor: '#fff', boxShadow: '0 8px 40px rgba(0,0,0,0.4)' }} onClick={(e) => e.stopPropagation()}>
            {/* Left: Editor provided images */}
            <div style={{ flex: 1, borderRight: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #e5e7eb', backgroundColor: '#f9fafb', fontSize: '0.78rem', fontWeight: 700, color: '#0f2d1f' }}>Editor Images ({Array.isArray(selectedArticle.imageUrls) ? selectedArticle.imageUrls.length : 0})</div>
              <div style={{ flex: 1, overflowY: 'auto', padding: '0.75rem', display: 'flex', flexWrap: 'wrap', gap: '8px', alignContent: 'flex-start' }}>
                {Array.isArray(selectedArticle.imageUrls) && selectedArticle.imageUrls.length > 0 ? (
                  selectedArticle.imageUrls.map((img: any, i: number) => {
                    const apiBase = (import.meta as any).env?.VITE_API_URL || 'http://localhost:3000';
                    const src = (img.url || '').startsWith('/') ? `${apiBase}${img.url}` : img.url;
                    return (
                      <div key={i} style={{ width: '120px', height: '120px', borderRadius: '6px', border: '1px solid #e5e7eb', overflow: 'hidden', cursor: 'pointer' }} onClick={() => { setLightboxSrc(src); setLightboxTitle(img.name || `Image ${i + 1}`); setLightboxArticle(selectedArticle); setLightboxSection(''); }}>
                        <img src={src} alt={img.name || ''} style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" />
                      </div>
                    );
                  })
                ) : (
                  <div style={{ padding: '2rem', color: '#999', fontSize: '0.82rem', textAlign: 'center', width: '100%' }}>No editor images uploaded.</div>
                )}
              </div>
            </div>
            {/* Right: Vendor uploaded file */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #e5e7eb', backgroundColor: '#f9fafb', fontSize: '0.78rem', fontWeight: 700, color: '#0f2d1f' }}>Vendor Upload</div>
              <div style={{ flex: 1, overflowY: 'auto', padding: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {selectedArticle.fileUrl ? (
                  (() => {
                    const apiBase = (import.meta as any).env?.VITE_API_URL || 'http://localhost:3000';
                    const src = selectedArticle.fileUrl!.startsWith('/') ? `${apiBase}${selectedArticle.fileUrl}` : selectedArticle.fileUrl!;
                    const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(selectedArticle.fileUrl!);
                    const isPdf = /\.pdf$/i.test(selectedArticle.fileUrl!);
                    if (isImage) {
                      return <img src={src} alt={selectedArticle.fileName || 'Vendor file'} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: '6px', cursor: 'pointer' }} onClick={() => { setLightboxSrc(src); setLightboxTitle(selectedArticle.fileName || 'Vendor Upload'); setLightboxArticle(selectedArticle); setLightboxSection(''); }} />;
                    }
                    if (isPdf) {
                      return (
                        <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
                          <iframe src={src} title={selectedArticle.fileName || 'PDF'} style={{ width: '100%', flex: 1, border: 'none', borderRadius: '6px' }} />
                          <div style={{ textAlign: 'center', marginTop: '6px' }}>
                            <button onClick={() => { setLightboxSrc(src); setLightboxTitle(selectedArticle.fileName || 'PDF Viewer'); setLightboxArticle(selectedArticle); setLightboxSection(''); }} style={{ padding: '4px 12px', fontSize: '0.72rem', backgroundColor: '#1a6b3c', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Expand</button>
                          </div>
                        </div>
                      );
                    }
                    return <a href={src} target="_blank" rel="noopener noreferrer" style={{ padding: '1rem', backgroundColor: '#f0fdf4', border: '1px solid #86efac', borderRadius: '8px', textDecoration: 'none', color: '#166534', fontWeight: 600, fontSize: '0.85rem' }}>📎 {selectedArticle.fileName || 'Download File'}</a>;
                  })()
                ) : (
                  <div style={{ color: '#999', fontSize: '0.82rem' }}>No vendor file uploaded.</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {!showGallery && !showMemorialTiles && showAddSection && (
        <div style={st.addForm}>
          <input style={st.input} placeholder="Section name..." value={newSectionName} onChange={e => setNewSectionName(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleAddSection()} autoFocus />
          <button style={st.btnSmall} onClick={handleAddSection}>Add</button>
          <button style={st.btnSec} onClick={() => { setShowAddSection(false); setNewSectionName(''); }}>Cancel</button>
        </div>
      )}
      {!showGallery && !showMemorialTiles && sections.length === 0 && !showAddSection && <div style={st.emptyState}>No sections yet. Click "+ Add Section".</div>}

      {!showGallery && !showMemorialTiles && sections.length > 0 && (
        <>
          {/* Mobile pane switcher */}
          {isMobile && (
            <div style={{ display: 'flex', gap: 0, marginBottom: '8px', borderRadius: '6px', overflow: 'hidden', border: '1px solid #e5e7eb' }}>
              {(['sections', 'articles', 'details'] as const).map(p => (
                <button key={p} onClick={() => setMobilePane(p)} style={{ flex: 1, padding: '8px', border: 'none', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', backgroundColor: mobilePane === p ? '#1a6b3c' : '#f9fafb', color: mobilePane === p ? '#fff' : '#555', textTransform: 'capitalize' }}>
                  {p === 'sections' ? `Sections (${sections.length})` : p === 'articles' ? `Articles${selectedSection ? ` (${articles.filter(a=>!a.isDeleted).length})` : ''}` : 'Details'}
                </button>
              ))}
            </div>
          )}

        <div style={isMobile ? { border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden', minHeight: '400px', backgroundColor: '#fff' } : st.threePane}>
          {/* PANE 1: Sections */}
          {(!isMobile || mobilePane === 'sections') && (
          <div style={st.pane1}>
            <div style={st.paneHead}>Sections ({sections.length})</div>
            <div style={st.pane1List}>
              {sections.map((section, sIdx) => {
                const isSel = selectedSectionId === section.id;
                const isHov = hoveredSectionId === section.id;
                const isEd = editingSectionId === section.id;
                const bg = isSel ? '#e8f5e9' : isHov ? '#f0f7f2' : 'transparent';
                return (
                  <div key={section.id} style={{ ...st.secRow, backgroundColor: bg, borderLeft: isSel ? '3px solid #1a6b3c' : '3px solid transparent' }}
                    onClick={() => { if (!isEd) { setSelectedSectionId(section.id); setSelectedArticleId(null); if (isMobile) setMobilePane('articles'); } }}
                    onMouseEnter={() => setHoveredSectionId(section.id)} onMouseLeave={() => setHoveredSectionId(null)} role="button" tabIndex={0}>
                    <span style={st.secNum}>{sIdx + 1}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {isEd ? (
                        <div style={{ display: 'flex', gap: '3px' }} onClick={e => e.stopPropagation()}>
                          <input style={{ ...st.input, fontSize: '0.78rem', padding: '3px 5px' }} value={editSectionName} onChange={e => setEditSectionName(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') handleRenameSection(section.id); if (e.key === 'Escape') setEditingSectionId(null); }} autoFocus />
                          <button style={st.btnSmall} onClick={() => handleRenameSection(section.id)}>✓</button>
                          <button style={st.btnSec} onClick={() => setEditingSectionId(null)}>✕</button>
                        </div>
                      ) : (<><div style={st.secName}>{section.name}</div><div style={st.secMeta}>{section.articles.filter(a => !a.isDeleted).length} articles</div></>)}
                    </div>
                    {canEdit && isSel && !isEd && (
                      <div style={st.secActions}>
                        <button style={st.iconBtn} onClick={e => { e.stopPropagation(); setEditingSectionId(section.id); setEditSectionName(section.name); }} aria-label="Rename">✏️</button>
                        <button style={st.moveBtn} onClick={e => { e.stopPropagation(); handleMoveSection(sIdx, 'up'); }} disabled={sIdx === 0} aria-label="Up">▲</button>
                        <button style={st.moveBtn} onClick={e => { e.stopPropagation(); handleMoveSection(sIdx, 'down'); }} disabled={sIdx === sections.length - 1} aria-label="Down">▼</button>
                        <button style={st.iconBtn} onClick={e => { e.stopPropagation(); handleDeleteSection(section.id); }} aria-label="Delete">🗑️</button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          )}

          {/* PANE 2: Articles Table */}
          {(!isMobile || mobilePane === 'articles') && (
          <div style={st.pane2}>
            <div style={st.paneHead}>
              <span>{selectedSection ? selectedSection.name : 'Select a section'}</span>
              {canEdit && selectedSection && <button style={st.btnSmall} onClick={() => setShowAddArticle(true)}>+ Article</button>}
            </div>
            {showAddArticle && selectedSection && (
              <div style={st.addForm}>
                <input style={st.input} placeholder="Article title..." value={newArticleTitle} onChange={e => setNewArticleTitle(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleAddArticle()} autoFocus />
                <button style={st.btnSmall} onClick={handleAddArticle}>Add</button>
                <button style={st.btnSec} onClick={() => { setShowAddArticle(false); setNewArticleTitle(''); }}>Cancel</button>
              </div>
            )}
            {!selectedSection && <div style={st.emptyMsg}>Select a section from the left.</div>}
            {selectedSection && articles.length === 0 && !showAddArticle && <div style={st.emptyMsg}>No articles yet.</div>}
            {selectedSection && articles.length > 0 && (
              <div style={st.tableWrap}>
                <table style={st.table}>
                  <thead><tr>
                    <th style={st.th}>#</th>
                    <th style={{ ...st.th, textAlign: 'left' }}>Title</th>
                    <th style={{ ...st.th, textAlign: 'left' }}>Author</th>
                    <th style={st.th}>Status</th>
                    <th style={st.th}>Added</th>
                    <th style={st.th}>Edited</th>
                    <th style={st.th}>File</th>
                    {canEdit && <th style={st.th}></th>}
                  </tr></thead>
                  <tbody>
                    {articles.map((article, aIdx) => {
                      const isSel = selectedArticleId === article.id;
                      const isHov = hoveredArticleId === article.id;
                      const deleted = article.isDeleted;
                      const rowBg = deleted ? '#fef2f2' : isSel ? '#e3f2fd' : isHov ? '#f5faff' : 'transparent';
                      const activeIdx = deleted ? '—' : articles.filter(a => !a.isDeleted).indexOf(article) + 1;
                      return (
                        <tr key={article.id} style={{ backgroundColor: rowBg, cursor: 'pointer', transition: 'background 0.1s', textDecoration: deleted ? 'line-through' : 'none', color: deleted ? '#999' : undefined }}
                          onClick={() => { setSelectedArticleId(article.id); if (isMobile) setMobilePane('details'); }}
                          onMouseEnter={() => setHoveredArticleId(article.id)} onMouseLeave={() => setHoveredArticleId(null)}>
                          <td style={st.td}>{activeIdx}</td>
                          <td style={{ ...st.td, textAlign: 'left', fontWeight: 500 }}>{article.title}</td>
                          <td style={{ ...st.td, textAlign: 'left', fontSize: '0.72rem', color: '#666' }}>{article.authorName || '—'}</td>
                          <td style={st.td}><span style={{ ...st.badge, backgroundColor: STATUS_COLORS[article.status] || '#666' }}>{article.status}</span></td>
                          <td style={st.td}>{fmtDate(article.createdAt)}</td>
                          <td style={st.td}>{article.updatedAt !== article.createdAt ? fmtDate(article.updatedAt) : '—'}</td>
                          <td style={st.td}>{article.fileName ? (article.fileAvailable === false ? <span title="File missing — needs re-upload">🔴</span> : '📎') : '—'}</td>
                          {canEdit && (
                            <td style={{ ...st.td, whiteSpace: 'nowrap' }}>
                              {deleted ? (
                                <button style={st.btnSmall} onClick={e => { e.stopPropagation(); handleRestore(article.id); }}>Restore</button>
                              ) : (<>
                                <button style={st.iconBtn} onClick={e => { e.stopPropagation(); openEditPopup(article); }} aria-label="Edit">✏️</button>
                                <button style={st.moveBtn} onClick={e => { e.stopPropagation(); handleMoveArticle(aIdx, 'up'); }} disabled={aIdx === 0} aria-label="Up">▲</button>
                                <button style={st.moveBtn} onClick={e => { e.stopPropagation(); handleMoveArticle(aIdx, 'down'); }} disabled={aIdx === articles.filter(a=>!a.isDeleted).length - 1} aria-label="Down">▼</button>
                                <button style={st.iconBtn} onClick={e => { e.stopPropagation(); handleSoftDelete(article.id); }} aria-label="Remove">🗑️</button>
                              </>)}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          )}

          {/* PANE 3: Details & Comments */}
          {(!isMobile || mobilePane === 'details') && (
          <div style={st.pane3}>
            <div style={st.paneHead}>Details &amp; Comments</div>
            {!selectedArticle ? <div style={st.emptyMsg}>Select an article to see details.</div> : (
              <div style={st.detailArea}>
                <div style={st.detailCard}>
                  <div style={st.detailTitle}>{selectedArticle.title}</div>
                  {selectedArticle.authorName && <div style={{ fontSize: '0.75rem', color: '#555', marginBottom: '4px' }}>by {selectedArticle.authorName}</div>}
                  <div style={st.detailMeta}>
                    <span style={{ ...st.badge, backgroundColor: STATUS_COLORS[selectedArticle.status] || '#666' }}>{selectedArticle.status}</span>
                    <span style={st.detailDate}>Added {fmtDate(selectedArticle.createdAt)}</span>
                  </div>
                  {selectedArticle.fileUrl && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      <a href={selectedArticle.fileUrl.startsWith('/') ? `${API_BASE}${selectedArticle.fileUrl}` : selectedArticle.fileUrl} target="_blank" rel="noopener noreferrer" style={st.fileLink}>📎 {selectedArticle.fileName || 'Document'}</a>
                      {selectedArticle.fileAvailable === false && <span title="File missing — needs re-upload" style={{ color: '#d32f2f', fontSize: '0.85rem', cursor: 'help' }}>🔴</span>}
                    </span>
                  )}
                  {Array.isArray(selectedArticle.imageUrls) && selectedArticle.imageUrls.length > 0 && (
                    <div style={{ marginTop: '8px' }}>
                      <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#555', marginBottom: '4px' }}>Images ({selectedArticle.imageUrls.length})</div>
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        {selectedArticle.imageUrls.map((img, i) => (
                          <a key={i} href={img.url} target="_blank" rel="noopener noreferrer" title={img.name}>
                            <img src={img.url} alt={img.name} style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '4px', border: '1px solid #e5e7eb' }} />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div style={st.commentSection}>
                  <div style={st.commentHeading}>📝 Editor Notes</div>
                  {selectedArticle.notes ? <div style={st.noteBox}>{selectedArticle.notes}</div> : <div style={st.noContent}>No editor notes.</div>}
                </div>
                {/* Design Suggestions */}
                {selectedArticle.designSuggestions && (
                  <div style={st.commentSection}>
                    <div style={st.commentHeading}>🎨 Design Suggestions</div>
                    <div style={{ fontSize: '0.8rem', color: '#333', backgroundColor: '#f0f4ff', padding: '0.5rem', borderRadius: '4px', lineHeight: 1.4 }} dangerouslySetInnerHTML={{ __html: selectedArticle.designSuggestions }} />
                  </div>
                )}
                {/* View Files Button */}
                {(selectedArticle.fileUrl || (Array.isArray(selectedArticle.imageUrls) && selectedArticle.imageUrls.length > 0)) && (
                  <div style={st.commentSection}>
                    <button onClick={() => setShowFileViewer(true)} style={{ padding: '0.4rem 0.8rem', fontSize: '0.78rem', fontWeight: 600, backgroundColor: '#0277bd', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                      📁 View Files
                    </button>
                  </div>
                )}
                <div style={st.commentSection}>
                  <div style={st.commentHeading}>💬 Vendor Comments</div>
                  {articleQuestions.length === 0 ? <div style={st.noContent}>No vendor comments.</div> : articleQuestions.map(q => (
                    <div key={q.id} style={st.commentCard}>
                      <div style={st.commentMeta}><span style={st.vendorName}>{q.vendorAccess?.vendorName || 'Vendor'}</span><span style={st.commentDate}>{fmtDate(q.createdAt)}</span></div>
                      <div style={st.commentText}>{q.question}</div>
                      {q.response && <div style={st.responseBox}><strong style={{ fontSize: '0.7rem', color: '#1a6b3c' }}>Response:</strong><div style={{ fontSize: '0.8rem', marginTop: '2px' }}>{q.response}</div></div>}
                    </div>
                  ))}
                </div>
                {/* Organizer Feedback to Vendor */}
                <div style={st.commentSection}>
                  <div style={st.commentHeading}>📣 Feedback to Vendor</div>
                  {!feedbackEditing ? (
                    <div>
                      {selectedArticle.vendorFeedback ? (
                        <div style={{ ...st.noteBox, backgroundColor: '#fff8e1', borderColor: '#fde68a' }}>{selectedArticle.vendorFeedback}</div>
                      ) : (
                        <div style={st.noContent}>No feedback for the vendor yet.</div>
                      )}
                      {canEdit && (
                        <button onClick={() => { setFeedbackText(selectedArticle.vendorFeedback || ''); setFeedbackEditing(true); }} style={{ marginTop: '0.4rem', padding: '0.3rem 0.6rem', fontSize: '0.75rem', fontWeight: 600, backgroundColor: '#f59e0b', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                          {selectedArticle.vendorFeedback ? '✏️ Edit Feedback' : '+ Add Feedback'}
                        </button>
                      )}
                    </div>
                  ) : (
                    <div>
                      <textarea value={feedbackText} onChange={(e) => setFeedbackText(e.target.value)} placeholder="Write feedback visible to the vendor..." rows={3} style={{ width: '100%', padding: '0.4rem', fontSize: '0.8rem', border: '1px solid #fde68a', borderRadius: '4px', resize: 'vertical', boxSizing: 'border-box', backgroundColor: '#fffdf5' }} />
                      <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.3rem' }}>
                        <button onClick={() => handleSaveFeedback(selectedArticle.id)} disabled={feedbackSaving} style={{ padding: '0.3rem 0.6rem', fontSize: '0.75rem', fontWeight: 600, backgroundColor: '#1a6b3c', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', opacity: feedbackSaving ? 0.5 : 1 }}>
                          {feedbackSaving ? 'Saving...' : 'Save'}
                        </button>
                        <button onClick={() => setFeedbackEditing(false)} style={{ padding: '0.3rem 0.6rem', fontSize: '0.75rem', backgroundColor: '#e0e0e0', color: '#333', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          )}
        </div>
        </>
      )}

      {/* EDIT ARTICLE POPUP */}
      {editPopupArticle && (
        <div style={st.overlay} onClick={() => setEditPopupArticle(null)}>
          <div style={st.modal} onClick={e => e.stopPropagation()}>
            <div style={st.modalHeader}>
              <h4 style={{ margin: 0, fontSize: '1rem', color: '#0f2d1f' }}>Edit Article</h4>
              <button style={st.closeBtn} onClick={() => setEditPopupArticle(null)} aria-label="Close">✕</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={st.label}>Title</label>
                <input style={st.input} value={epTitle} onChange={e => setEpTitle(e.target.value)} />
              </div>
              <div>
                <label style={st.label}>Author</label>
                <input style={st.input} value={epAuthor} onChange={e => setEpAuthor(e.target.value)} placeholder="Author name" />
              </div>
              <div>
                <label style={st.label}>Status</label>
                <select style={st.input} value={epStatus} onChange={e => setEpStatus(e.target.value)}>
                  {ARTICLE_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={st.label}>Editor Notes</label>
                <textarea style={{ ...st.input, minHeight: '60px', resize: 'vertical' }} value={epNotes} onChange={e => setEpNotes(e.target.value)} />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={st.label}>Design Suggestions</label>
                <div style={{ display: 'flex', gap: '2px', padding: '3px 5px', backgroundColor: '#f3f4f6', borderRadius: '4px 4px 0 0', border: '1px solid #ccc', borderBottom: 'none' }}>
                  <button type="button" onClick={() => document.execCommand('bold')} style={{ padding: '2px 6px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 700 }}>B</button>
                  <button type="button" onClick={() => document.execCommand('italic')} style={{ padding: '2px 6px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '0.78rem', fontStyle: 'italic' }}>I</button>
                  <button type="button" onClick={() => document.execCommand('underline')} style={{ padding: '2px 6px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '0.78rem', textDecoration: 'underline' }}>U</button>
                  <button type="button" onClick={() => document.execCommand('insertUnorderedList')} style={{ padding: '2px 6px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '0.78rem' }}>•</button>
                </div>
                <div
                  contentEditable
                  dangerouslySetInnerHTML={{ __html: epDesignSuggestions }}
                  onInput={(e) => setEpDesignSuggestions((e.target as HTMLDivElement).innerHTML)}
                  onBlur={(e) => setEpDesignSuggestions((e.target as HTMLDivElement).innerHTML)}
                  style={{ ...st.input, minHeight: '70px', border: '1px solid #ccc', borderRadius: '0 0 4px 4px', outline: 'none', lineHeight: 1.4 }}
                  suppressContentEditableWarning
                />
              </div>
            </div>
            {/* Document file — Drag & Drop + Browse + Google Drive */}
            <div style={{ marginBottom: '12px' }}>
              <label style={st.label}>Document (PDF/Word/Image)</label>
              <FileDropZone
                currentFile={epFileName ? { url: epFileUrl, name: epFileName } : null}
                onFileUploaded={(url, name) => { setEpFileUrl(url); setEpFileName(name); }}
                onClear={() => { setEpFileUrl(''); setEpFileName(''); }}
              />
            </div>
            {/* Images — multiple uploads */}
            <div style={{ marginBottom: '16px' }}>
              <label style={st.label}>Images <button type="button" style={{ ...st.btnSmall, marginLeft: '8px' }} onClick={addImageEntry}>+ Add Image URL</button></label>
              {epImageUrls.map((img, idx) => (
                <div key={idx} style={{ display: 'flex', gap: '6px', marginTop: '6px', alignItems: 'center' }}>
                  <ImageDropZone
                    currentFile={img.url ? img : null}
                    onFileUploaded={(url, name) => { updateImageEntry(idx, 'url', url); updateImageEntry(idx, 'name', name); }}
                    onClear={() => removeImageEntry(idx)}
                  />
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button style={st.btn} onClick={handleSaveEdit} disabled={epSaving}>{epSaving ? 'Saving...' : 'Save Changes'}</button>
              <button style={st.btnSec} onClick={() => setEditPopupArticle(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── File Drop Zone Component ─────────────────────────────────

function FileDropZone({ currentFile, onFileUploaded, onClear }: {
  currentFile: { url: string; name: string } | null;
  onFileUploaded: (url: string, name: string) => void;
  onClear: () => void;
}) {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  async function uploadFile(file: File) {
    setUploading(true);
    try {
      const data = await apiUploadFile<{ url: string; fileName: string }>('/production-layout/upload', file, 'file');
      onFileUploaded(data.url, data.fileName);
    } catch { alert('File upload failed. Try again or paste a URL.'); }
    finally { setUploading(false); }
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault(); setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) uploadFile(file);
  }

  function handleBrowse(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) uploadFile(file);
  }

  function handleLinkSubmit() {
    if (!linkUrl.trim()) return;
    const name = linkUrl.includes('drive.google.com') ? 'Google Drive file' : linkUrl.split('/').pop() || 'linked-file';
    onFileUploaded(linkUrl.trim(), name);
    setShowLinkInput(false); setLinkUrl('');
  }

  if (currentFile) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px', backgroundColor: '#f0fdf4', borderRadius: '6px', border: '1px solid #d1fae5' }}>
        <span style={{ fontSize: '1.2rem' }}>📎</span>
        <span style={{ flex: 1, fontSize: '0.8rem', fontWeight: 500 }}>{currentFile.name}</span>
        <button type="button" style={{ ...st.iconBtn, color: '#d32f2f' }} onClick={onClear} aria-label="Remove file">✕</button>
      </div>
    );
  }

  return (
    <div>
      <div
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        style={{ padding: '16px', border: `2px dashed ${dragging ? '#1a6b3c' : '#ccc'}`, borderRadius: '8px', textAlign: 'center', backgroundColor: dragging ? '#f0fdf4' : '#fafafa', cursor: 'pointer', transition: 'all 0.2s' }}
        onClick={() => fileInputRef.current?.click()}
      >
        <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={handleBrowse} accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.gif,.webp" />
        {uploading ? (
          <span style={{ fontSize: '0.82rem', color: '#1a6b3c' }}>Uploading...</span>
        ) : (
          <div>
            <div style={{ fontSize: '1.5rem', marginBottom: '4px' }}>📂</div>
            <div style={{ fontSize: '0.8rem', color: '#555' }}>Drag & drop a file here, or <strong>click to browse</strong></div>
            <div style={{ fontSize: '0.7rem', color: '#999', marginTop: '4px' }}>PDF, Word, images up to 25MB</div>
          </div>
        )}
      </div>
      <div style={{ display: 'flex', gap: '8px', marginTop: '6px', alignItems: 'center' }}>
        <button type="button" style={st.btnSec} onClick={() => setShowLinkInput(!showLinkInput)}>
          🔗 Paste URL / Google Drive link
        </button>
      </div>
      {showLinkInput && (
        <div style={{ display: 'flex', gap: '6px', marginTop: '6px' }}>
          <input style={{ ...st.input, flex: 1 }} placeholder="https://drive.google.com/file/d/... or any URL" value={linkUrl} onChange={e => setLinkUrl(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleLinkSubmit()} autoFocus />
          <button type="button" style={st.btnSmall} onClick={handleLinkSubmit}>Add</button>
        </div>
      )}
    </div>
  );
}

function ImageDropZone({ currentFile, onFileUploaded, onClear }: {
  currentFile: { url: string; name: string } | null;
  onFileUploaded: (url: string, name: string) => void;
  onClear: () => void;
}) {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  async function uploadFile(file: File) {
    setUploading(true);
    try {
      const data = await apiUploadFile<{ url: string; fileName: string }>('/production-layout/upload', file, 'file');
      onFileUploaded(data.url, data.fileName);
    } catch { alert('Image upload failed.'); }
    finally { setUploading(false); }
  }

  if (currentFile) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1 }}>
        <img src={currentFile.url.startsWith('/') ? `${API_BASE}${currentFile.url}` : currentFile.url} alt="" style={{ width: '32px', height: '32px', objectFit: 'cover', borderRadius: '4px' }} />
        <span style={{ flex: 1, fontSize: '0.75rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{currentFile.name}</span>
        <button type="button" style={st.iconBtn} onClick={onClear} aria-label="Remove">✕</button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1 }}>
      <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={e => { const f = e.target.files?.[0]; if (f) uploadFile(f); }} accept="image/*" />
      <button type="button" style={st.btnSec} onClick={() => fileInputRef.current?.click()}>
        {uploading ? '...' : '📷 Upload image'}
      </button>
    </div>
  );
}

function fmtDate(isoStr: string): string {
  if (!isoStr) return '—';
  return new Date(isoStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const st: Record<string, React.CSSProperties> = {
  container: { padding: 0 },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' },
  title: { fontSize: '1.1rem', fontWeight: 700, color: '#0f2d1f', margin: 0 },
  btn: { padding: '0.5rem 1rem', backgroundColor: '#1a6b3c', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' },
  btnSmall: { padding: '0.25rem 0.5rem', backgroundColor: '#1a6b3c', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer' },
  btnSec: { padding: '0.25rem 0.5rem', backgroundColor: '#e0e0e0', color: '#333', border: 'none', borderRadius: '4px', fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer' },
  iconBtn: { background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', fontSize: '0.8rem', lineHeight: 1 },
  moveBtn: { background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.7rem', padding: '2px 3px', color: '#666' },
  error: { color: '#d32f2f', backgroundColor: '#fdecea', padding: '0.5rem', borderRadius: '4px', marginBottom: '0.75rem', fontSize: '0.85rem' },
  emptyState: { padding: '2rem', textAlign: 'center' as const, color: '#666', fontSize: '0.9rem' },
  addForm: { display: 'flex', gap: '0.5rem', alignItems: 'center', padding: '0.5rem 0.6rem', backgroundColor: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb', marginBottom: '0.5rem' },
  input: { padding: '0.35rem 0.5rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.82rem', width: '100%', boxSizing: 'border-box' as const },
  label: { display: 'block', fontSize: '0.72rem', fontWeight: 600, color: '#555', marginBottom: '4px', textTransform: 'uppercase' as const },
  threePane: { display: 'grid', gridTemplateColumns: '200px 1fr 260px', gap: 0, border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden', minHeight: '500px', backgroundColor: '#fff' },
  pane1: { borderRight: '1px solid #e5e7eb', backgroundColor: '#f9fafb', display: 'flex', flexDirection: 'column' as const },
  pane2: { display: 'flex', flexDirection: 'column' as const, overflow: 'hidden' },
  pane3: { borderLeft: '1px solid #e5e7eb', backgroundColor: '#fafbfc', display: 'flex', flexDirection: 'column' as const, overflow: 'hidden' },
  paneHead: { padding: '0.5rem 0.6rem', fontWeight: 600, fontSize: '0.7rem', color: '#555', textTransform: 'uppercase' as const, letterSpacing: '0.4px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 },
  emptyMsg: { padding: '2rem 0.75rem', textAlign: 'center' as const, color: '#999', fontSize: '0.8rem' },
  pane1List: { flex: 1, overflowY: 'auto' as const },
  secRow: { padding: '0.5rem 0.6rem', cursor: 'pointer', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: '0.4rem', transition: 'background 0.1s' },
  secNum: { width: '18px', height: '18px', borderRadius: '50%', backgroundColor: '#e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem', fontWeight: 700, flexShrink: 0 },
  secName: { fontSize: '0.8rem', fontWeight: 600, color: '#0f2d1f', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const },
  secMeta: { fontSize: '0.65rem', color: '#888' },
  secActions: { display: 'flex', gap: '1px', flexShrink: 0 },
  tableWrap: { flex: 1, overflowY: 'auto' as const, overflowX: 'auto' as const },
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: '0.78rem' },
  th: { padding: '6px 8px', borderBottom: '2px solid #e5e7eb', fontSize: '0.68rem', fontWeight: 600, color: '#666', textTransform: 'uppercase' as const, textAlign: 'center' as const, whiteSpace: 'nowrap' as const },
  td: { padding: '6px 8px', borderBottom: '1px solid #f3f3f3', textAlign: 'center' as const, verticalAlign: 'middle' as const },
  badge: { padding: '2px 6px', borderRadius: '3px', fontSize: '0.62rem', fontWeight: 600, color: '#fff', display: 'inline-block' },
  detailArea: { flex: 1, overflowY: 'auto' as const, padding: '0.6rem' },
  detailCard: { padding: '0.6rem', backgroundColor: '#fff', borderRadius: '6px', border: '1px solid #e5e7eb', marginBottom: '0.75rem' },
  detailTitle: { fontSize: '0.85rem', fontWeight: 600, color: '#0f2d1f', marginBottom: '2px' },
  detailMeta: { display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '6px' },
  detailDate: { fontSize: '0.7rem', color: '#888' },
  fileLink: { display: 'inline-block', fontSize: '0.75rem', color: '#1a6b3c', fontWeight: 500, textDecoration: 'none' },
  commentSection: { marginBottom: '0.75rem' },
  commentHeading: { fontSize: '0.72rem', fontWeight: 700, color: '#333', marginBottom: '6px', textTransform: 'uppercase' as const },
  noteBox: { fontSize: '0.8rem', color: '#444', backgroundColor: '#fffde7', padding: '0.5rem', borderRadius: '4px', border: '1px solid #fff9c4', whiteSpace: 'pre-wrap' as const },
  noContent: { fontSize: '0.75rem', color: '#bbb', fontStyle: 'italic' },
  commentCard: { padding: '0.5rem', backgroundColor: '#fff', borderRadius: '6px', border: '1px solid #e5e7eb', marginBottom: '0.5rem' },
  commentMeta: { display: 'flex', justifyContent: 'space-between', marginBottom: '3px' },
  vendorName: { fontSize: '0.7rem', fontWeight: 600, color: '#1a6b3c' },
  commentDate: { fontSize: '0.65rem', color: '#999' },
  commentText: { fontSize: '0.8rem', color: '#333' },
  responseBox: { marginTop: '6px', padding: '0.4rem', backgroundColor: '#f0fdf4', borderRadius: '4px', border: '1px solid #d1fae5' },
  // Modal
  overlay: { position: 'fixed' as const, inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { backgroundColor: '#fff', borderRadius: '12px', padding: '1.5rem', width: '680px', maxWidth: '95vw', maxHeight: '85vh', overflowY: 'auto' as const },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' },
  closeBtn: { background: 'none', border: 'none', fontSize: '1.1rem', cursor: 'pointer', color: '#666', padding: '2px 6px' },
};
